import { Flow } from 'flow-to-typescript-codemod';
import { useMemo } from 'react';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { useSplitClient } from 'modules/feature-flags';
import { flagLoading, resolveFlag } from './resolveFlag';
import type { FeatureFlag } from './featureFlags.generated';

export function useSplitFlag<T = string>(
  flagName: FeatureFlag,
  castTo?: (arg1: string) => T
): [T, boolean];

export function useSplitFlag(flagName: FeatureFlag, castTo?: void): [string, boolean];

// @ts-expect-error [EN-10263] - TS7006 - Parameter 'flagName' implicitly has an 'any' type. | TS7006 - Parameter 'castTo' implicitly has an 'any' type.
export function useSplitFlag(flagName, castTo) {
  const [client, ready, timeout] = useSplitClient();
  const loading = flagLoading(ready, timeout);
  const flag = useMemo(
    () => resolveFlag(client, ready, timeout, flagName, false).treatment,
    [client, ready, timeout, flagName]
  );
  return [castTo ? castTo(flag) : flag, loading];
}

export const useSplitFlags = (
  flagNames: FeatureFlag[]
): [
  Nullable<{
    [x: string]: string;
  }>,
  boolean,
] => {
  const [client, ready, timeout] = useSplitClient();
  const loading = flagLoading(ready, timeout);
  // Callers usually pass a fresh array each render; key on its contents.
  const flagNamesKey = flagNames.join(',');
  const flags = useMemo(() => {
    const names = flagNamesKey === '' ? [] : flagNamesKey.split(',');
    if (ready && names.length > 0) {
      const values = client?.getTreatments(names);
      if (values != null) {
        Object.keys(values).forEach((flag) => {
          analytics.addContext(globalContext.workspace.featureFlag(flag), values[flag]);
        });
      }
      return values;
    }
    return null;
  }, [client, flagNamesKey, ready]);
  return [flags, loading];
};
export const withSplitFlags: (
  flagNames: FeatureFlag[],
  prop: string
) => (
  Component: Flow.AbstractComponent<
    {
      [p: string]: {
        loading: boolean;
        flags: unknown;
      };
    },
    unknown
  >
) => (props: Record<any, any>) => React.ReactNode =
  (flagNames, prop = 'treatments') =>
  (
    Component: Flow.AbstractComponent<
      {
        [p: string]: {
          flags: unknown;
          loading: boolean;
        };
      },
      unknown
    >
  ) =>
    function WithSplitFlags(props: Record<any, any>) {
      const [flags, loading] = useSplitFlags(flagNames);
      return <Component {...props} {...{ [prop]: { loading, flags } }} />;
    };
